import { useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import axiosInstance, { getUserDetails,
  handleLegacySessionExpired,
  isCompanyManagerUser,
} from '../../components/content/axiosConfig';
import Preloader from '../../components/custom/preLoader';
import showErrorToast, { showSuccessToast } from '../../components/toast/toast';
import { ForageDropDownSelection, TypeDropDownSelection, WaterSupplyDropDownSelection } from '../../components/custom/DropDownSelections';
import FishSpeciesDropDownSelection from '../../components/custom/FishSpeciesDropDownSelection';
import AgeOfPondSelector from '../../components/custom/AgeOfPondSelector';
import DateWithUnknownSelector from '../../components/custom/DateWithUnknownSelector';
import SupplementalStockingDates from '../../components/custom/SupplementalStockingDates';
import { toDateInputValue } from '../../utils/toDateInputValue';
import { resolveScopedFisherID } from '../../utils/pondListForUser';
import { getScopedAccount } from '../../utils/accountContext';

/**
 * Optional Create Pond UI blocks — hidden by default. Stocking (prey/predator/forage/tags) is only on
 * Update Pond → Stocking Data (`/pondStocking/:id`), not on this page.
 * Set any flag to `true` to show that block again. State, handlers, and submit payloads are unchanged.
 */
const SHOW_CREATE_POND_AGE_OF_POND = false;
const SHOW_CREATE_POND_ESTIMATED_INITIAL_STOCKING_DATE = false;
const SHOW_CREATE_POND_SUPPLEMENTAL_STOCKING_DATES = false;
const SHOW_CREATE_POND_BEGIN_MANAGEMENT_DATE = false;
const SHOW_CREATE_POND_FORAGE_MULTISELECT = true;
const SHOW_CREATE_POND_ADDITIONAL_FORAGE_TYPE = true;
const SHOW_CREATE_POND_AQUATIC_VEGETATION = false;

const showDatesManagementCard =
  SHOW_CREATE_POND_AGE_OF_POND ||
  SHOW_CREATE_POND_ESTIMATED_INITIAL_STOCKING_DATE ||
  SHOW_CREATE_POND_SUPPLEMENTAL_STOCKING_DATES ||
  SHOW_CREATE_POND_BEGIN_MANAGEMENT_DATE;

const CreatePond = () => {
  const history = useHistory();
  const userDetails = getUserDetails();
  const isCompanyManager = isCompanyManagerUser(userDetails);
  const fisherID = resolveScopedFisherID(userDetails);
  const scopedAccount = getScopedAccount();
  const sandboxFisherId =
    userDetails?.sandboxFisherId != null && userDetails.sandboxFisherId !== ""
      ? Number(userDetails.sandboxFisherId)
      : null;
  const creatingOnSandbox =
    isCompanyManager &&
    !scopedAccount &&
    sandboxFisherId != null &&
    Number(fisherID) === sandboxFisherId;
  const companyNeedsAccount =
    isCompanyManager && (fisherID == null || fisherID === "");
  const targetAccountLabel =
    scopedAccount?.name ||
    (creatingOnSandbox
      ? userDetails?.sandboxFisherName || "Company Demo"
      : null);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedFishSpecies, setSelectedFishSpecies] = useState([]);
  const [selectedForage, setSelectedForage] = useState([]); // Array for multiple selection
  const [selectedType, setSelectedType] = useState(null);
  const [selectedWaterSupply, setSelectedWaterSupply] = useState([]); // Changed to array for multiple selection
  const [formData, setFormData] = useState({
    Name: '',
    Location: '',
    FishSpecies: [],
    Forage: null,
    ForageType: '',
    Type: null,
    Age: '',
    AgeInYears: null,
    AgeType: null,
    Size: '',
    Depth: '',
    MaximumDepth: '',
    AverageDepth: '',
    EstimatedVolume: null,
    EstimatedInitialStockingDate: null,
    InitialStockingDateType: null,
    BeginManagementDate: '',
    SupplementalStockingDates: [],
    AquaticVegetationPresent: '',
    WaterSupply: null, // Will store comma-separated string
    FisherID: fisherID,
    // Not shown on this page — kept for API parity / future use (same as before)
    Weight_Unit: null,
    Length_Unit: null,
  });

  const handleInputChange = (e) => {
    const { id, value, type, checked } = e.target;
    setFormData((prev) => {
      const updated = {
        ...prev,
        [id]: type === 'checkbox' ? checked : value,
      };
      
      // Calculate EstimatedVolume when AverageDepth or Size changes
      if (id === 'AverageDepth' || id === 'Size') {
        const avgDepth = id === 'AverageDepth' ? parseFloat(value) : parseFloat(prev.AverageDepth);
        const size = id === 'Size' ? parseFloat(value) : parseFloat(prev.Size);
        if (avgDepth && size) {
          updated.EstimatedVolume = avgDepth * size;
        } else {
          updated.EstimatedVolume = null;
        }
      }
      
      return updated;
    });
  };

  const handleApiError = (error, defaultMessage) => {
    if (error.response && error.response.status === 401) {
      const auth = handleLegacySessionExpired(error);
      showErrorToast(auth.message);
    } else {
      const errorMessage = error.response?.data?.message || defaultMessage;
      showErrorToast(errorMessage);
      console.error(defaultMessage, error);
    }
  };

  const handleFishSpeciesChange = (selectedValues) => {
    
    setSelectedFishSpecies(selectedValues);
    setFormData((prev) => ({
      ...prev,
      FishSpecies: selectedValues,
    }));

    setTimeout(() => {
    }, 2000
    );
  };

  // Handler for multiple forage selection
  const handleForageChange = (selectedOptions) => {
    // selectedOptions will be an array when allowMultiple is true
    const forageValues = selectedOptions ? selectedOptions.map(option => option.value) : [];
    const forageString = forageValues.join(',');

    setSelectedForage(forageValues);
    setFormData((prev) => ({
      ...prev,
      Forage: forageString, // Store as comma-separated string in formData
    }));
  };

  const handleTypeChange = (selectedOption) => {
    const typeValue = selectedOption ? selectedOption.value : null;
    setSelectedType(typeValue);
    setFormData((prev) => ({
      ...prev,
      Type: typeValue,
    }));
  };

  // Handler for multiple water supply selection
  const handleWaterSupplyChange = (selectedOptions) => {
    // selectedOptions will be an array when allowMultiple is true
    const waterSupplyValues = selectedOptions ? selectedOptions.map(option => option.value) : [];
    const waterSupplyString = waterSupplyValues.join(',');

    setSelectedWaterSupply(waterSupplyValues);
    setFormData((prev) => ({
      ...prev,
      WaterSupply: waterSupplyString, // Store as comma-separated string in formData
    }));
  };

  // Handler for Age of Pond
  const handleAgeOfPondChange = (ageData) => {
    setFormData((prev) => ({
      ...prev,
      AgeType: ageData.ageType,
      AgeInYears: ageData.ageInYears,
    }));
  };

  // Handler for Estimated Initial Stocking Date
  const handleInitialStockingDateChange = (dateData) => {
    setFormData((prev) => ({
      ...prev,
      InitialStockingDateType: dateData.dateType,
      EstimatedInitialStockingDate: dateData.dateValue,
    }));
  };

  // Handler for Supplemental Stocking Dates
  const handleSupplementalStockingDatesChange = (dates) => {
    setFormData((prev) => ({
      ...prev,
      SupplementalStockingDates: dates,
    }));
  };

  const validateForm = () => {
    if (fisherID == null || fisherID === '') {
      showErrorToast(
        isCompanyManager
          ? 'Open a Demo or client account from Company Accounts, then create a pond.'
          : scopedAccount
            ? 'Unable to resolve the active account. Leave and reopen the account, then try again.'
            : 'Unable to resolve your organization. Please sign in again.'
      );
      return false;
    }
    if (!formData.Name.trim()) {
      showErrorToast('Pond name is required');
      return false;
    }
    if (!formData.Location.trim()) {
      showErrorToast('Location is required');
      return false;
    }
    if (!formData.Type) {
      showErrorToast('Pond type is required');
      return false;
    }
    if (!formData.Size.trim()) {
      showErrorToast('Pond size is required');
      return false;
    }
    if (!formData.MaximumDepth.trim()) {
      showErrorToast('Maximum depth is required');
      return false;
    }
    return true;
  };

  const AddPondFishes = async (pondID) => {
    try {
      // Validate FishSpecies
      if (!formData.FishSpecies || !Array.isArray(formData.FishSpecies) || formData.FishSpecies.length === 0) {
        return { message: 'No fish species to add' };
      }

      // Construct payload for BulkAddPondFishes
      const pondFishes = formData.FishSpecies.map((fishTypeID) => ({
        PondID: pondID,
        FishTypeID: parseInt(fishTypeID, 10),
        FisherID: fisherID,
      }));

      // Send bulk insert request
      const response = await axiosInstance.post('/api/PondFishes/BulkAddPondFishes', {
        pondFishes,
      });

      showSuccessToast(`${response.data.message || 'Fish species added successfully'}`);
      return response.data;
    } catch (error) {
      // Show non-blocking error to allow redirect
      showErrorToast(
        error.response?.data?.message || 'Failed to add fish species. You can add them in the update pond page.'
      );
      // Re-throw to let handleSubmit handle loading state
      throw error;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    if (!validateForm()) {
      setIsLoading(false);
      return;
    }

    try {
      const payload = {
        Name: formData.Name,
        Location: formData.Location,
        Forage: formData.Forage,
        ForageType: formData.ForageType,
        Type: formData.Type,
        Age: formData.Age ? formData.Age : null,
        AgeInYears: formData.AgeInYears != null && formData.AgeInYears !== ""
          ? formData.AgeInYears
          : null,
        AgeType: formData.AgeType || null,
        Size: parseFloat(formData.Size) || 0,
        Depth: parseFloat(formData.MaximumDepth) || 0, // Keep Depth for backward compatibility
        MaximumDepth: parseFloat(formData.MaximumDepth) || 0,
        AverageDepth: formData.AverageDepth ? parseFloat(formData.AverageDepth) : null,
        EstimatedVolume: formData.EstimatedVolume,
        EstimatedInitialStockingDate: formData.EstimatedInitialStockingDate
          ? toDateInputValue(formData.EstimatedInitialStockingDate) || null
          : null,
        InitialStockingDateType: formData.InitialStockingDateType,
        BeginManagementDate: toDateInputValue(formData.BeginManagementDate) || null,
        SupplementalStockingDates: (formData.SupplementalStockingDates || []).map((sd) => ({
          ...sd,
          StockingDate: sd.StockingDate ? toDateInputValue(sd.StockingDate) || null : null,
        })),
        AquaticVegetationPresent: formData.AquaticVegetationPresent,
        WaterSupply: formData.WaterSupply, // This will be the comma-separated string
        FisherID: fisherID
      };

      const response = await axiosInstance.post('/api/Pond/AddPond', payload);
      const pondId = response.data.Pond.id;

      try {
        await AddPondFishes(pondId);
      } catch (_) {
        /* toast already shown in AddPondFishes */
      }

      showSuccessToast('Pond created successfully');
      history.push(`/updatePond/${pondId}`);
    } catch (error) {
      const data = error.response?.data;
      if (error.response?.status === 403 && data?.code === 'SANDBOX_POND_LIMIT') {
        setIsLoading(false);
        showErrorToast(
          data.message ||
            'The Demo account has reached its pond limit. Open a paid client account to add more ponds.'
        );
        return;
      }
      if (error.response?.status === 403 && data?.code === 'COMPANY_EXTRA_POND_REQUIRED') {
        setIsLoading(false);
        const path = data.paymentPath;
        if (path) {
          const go = window.confirm(
            `${data.message || 'Pond limit reached.'}\n\nOpen Buy extra ponds for this account?`
          );
          if (go) history.push(path);
        } else {
          showErrorToast(
            data.message ||
              'You have reached your pond limit. Ask your Pond Management Company to purchase an extra pond slot.'
          );
        }
        return;
      }
      if (error.response?.status === 403 && data?.code === 'EXTRA_POND_PAYMENT_REQUIRED') {
        setIsLoading(false);
        const extraAmount = data.extraPondAmount ?? 100;
        const go = window.confirm(
          `${data.message || 'You need an additional pond slot.'}\n\nPurchase an extra pond slot for $${extraAmount}/year?`
        );
        if (go) {
          history.push('/payment/extra-pond?return=/createPond');
        }
        return;
      }
      if (error.response?.status === 403 && data?.code === 'POND_LIMIT_REACHED') {
        setIsLoading(false);
        const go = window.confirm(
          `${data.message || 'Your package allows one pond.'}\n\nView upgrade options?`
        );
        if (go) {
          history.push('/packages');
        }
        return;
      }
      handleApiError(error, 'Failed to create pond');
    } finally {
      setIsLoading(false);
    }
  };

  return isLoading ? (
    <Preloader />
  ) : companyNeedsAccount ? (
    <div className="donezo-page donezo-workflow-page py-5">
      <div className="container-fluid px-3 px-md-4" style={{ maxWidth: 520 }}>
        <div className="card border-0 shadow-sm text-center p-4 p-md-5" style={{ borderRadius: 14 }}>
          <h1 className="h4 fw-semibold mb-2">Select an account first</h1>
          <p className="text-muted small mb-4">
            Open your Demo account or a paid client from Company Accounts, then create a pond for that organization.
          </p>
          <Link to="/company/accounts" className="btn btn-primary rounded-pill px-4">
            Company Accounts
          </Link>
        </div>
      </div>
    </div>
  ) : (
    <div className="donezo-page donezo-workflow-page py-3">
      <form onSubmit={handleSubmit}>
        <div className="container-fluid px-3 px-md-4">
          {/* Header Section */}
          <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between mb-4">
            <div className="mb-3 mb-md-0">
              <ul className="breadcrumb mb-2 small">
                <li className="breadcrumb-item">
                  <Link to="/">Home</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="/pond">Pond</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">Create Pond</li>
              </ul>
              <h1 className="mb-0 page-header donezo-page-header-title">Create Pond</h1>
              <p className="text-muted small mb-0 mt-2 donezo-page-header-subtitle" style={{ maxWidth: '640px' }}>
                After you submit, add or update stocking (prey, predator, tags) and stocking visits under{' '}
                <strong>Update Pond → Stocking Data</strong>.
              </p>
              {targetAccountLabel ? (
                <div
                  className={`alert ${creatingOnSandbox ? 'alert-warning' : 'alert-info'} py-2 px-3 small mt-3 mb-0`}
                  role="status"
                >
                  {creatingOnSandbox ? (
                    <>
                      Creating on the <strong>Demo</strong> account: {targetAccountLabel}. To create for a client,
                      open that account from{' '}
                      <Link to="/company/accounts">Company Accounts</Link> first.
                    </>
                  ) : (
                    <>
                      Creating pond for <strong>{targetAccountLabel}</strong>.
                    </>
                  )}
                </div>
              ) : null}
            </div>
            <button
              type="submit"
              className="btn btn-success px-4 py-2 rounded-pill shadow-sm"
              style={{
                fontSize: '0.9rem',
                fontWeight: '500',
                transition: 'all 0.3s ease',
              }}
              onMouseOver={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; }}
              onMouseOut={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
            >
              <i className="fa fa-plus me-2"></i>
              Submit
            </button>
          </div>

          <div className="row g-3">
            {/* Basic Information Panel */}
            <div className="col-12 col-lg-6">
              <div className="card border-0 shadow-sm donezo-workflow-card">
                <div className="card-header border-0 bg-transparent px-3 py-2 donezo-workflow-card__header">
                  <h5 className="mb-0 d-flex align-items-center donezo-workflow-card__title">
                    <i className="fa fa-info-circle me-2 text-primary" style={{ fontSize: '0.9rem' }}></i>
                    Basic Information
                  </h5>
                </div>
                <div className="card-body px-3 py-2">
                  <div className="row g-2">
                    <div className="col-12">
                      <label htmlFor="Name" className="form-label small mb-1">
                        Pond Name <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control mb-2"
                        id="Name"
                        value={formData.Name}
                        onChange={handleInputChange}
                      />
                    </div>

                    <div className="col-12">
                      <label htmlFor="Location" className="form-label small mb-1">
                        Location <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control mb-2"
                        id="Location"
                        value={formData.Location}
                        onChange={handleInputChange}
                      />
                    </div>

                    <div className="col-12 col-md-6 d-flex flex-column">
                      <div className="mb-2 flex-grow-1">
                        <TypeDropDownSelection
                          selectedValue={selectedType}
                          onChange={handleTypeChange}
                        />
                      </div>
                    </div>

                    <div className="col-12 col-md-6 d-flex flex-column">
                      <div className="mb-2 flex-grow-1">
                        <WaterSupplyDropDownSelection
                          allowMultiple={true}
                          selectedValue={selectedWaterSupply}
                          onChange={handleWaterSupplyChange}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Fish species + pond-level forage */}
            <div className="col-12 col-lg-6">
              <div className="card border-0 shadow-sm donezo-workflow-card">
                <div className="card-header border-0 bg-transparent px-3 py-2 donezo-workflow-card__header">
                  <h5 className="mb-0 d-flex align-items-center donezo-workflow-card__title">
                    <i className="fa fa-fish me-2 text-info" style={{ fontSize: '0.9rem' }}></i>
                    Fish species
                  </h5>
                </div>
                <div className="card-body px-3 py-2">
                  <div className="mb-2">
                    <FishSpeciesDropDownSelection
                      allowMultiple={true}
                      selectedValue={selectedFishSpecies}
                      onChange={handleFishSpeciesChange}
                    />
                  </div>

                  <div
                    className={`mb-2 ${SHOW_CREATE_POND_FORAGE_MULTISELECT ? '' : 'd-none'}`}
                    aria-hidden={!SHOW_CREATE_POND_FORAGE_MULTISELECT}
                  >
                    <ForageDropDownSelection
                      allowMultiple={true}
                      selectedValue={selectedForage}
                      onChange={handleForageChange}
                    />
                  </div>

                  {/* Hidden when SHOW_CREATE_POND_ADDITIONAL_FORAGE_TYPE is false — field still in formData / payload */}
                  <div
                    className={`mb-0 ${SHOW_CREATE_POND_ADDITIONAL_FORAGE_TYPE ? '' : 'd-none'}`}
                    aria-hidden={!SHOW_CREATE_POND_ADDITIONAL_FORAGE_TYPE}
                  >
                    <label htmlFor="ForageType" className="form-label small mb-1">
                      Additional Forage Type(s)
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="ForageType"
                      value={formData.ForageType}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Dates & Management — entire card omitted when all section flags are false (avoids empty card) */}
            {showDatesManagementCard && (
            <div className="col-12 col-lg-6">
              <div className="card border-0 shadow-sm h-100 donezo-workflow-card">
                <div className="card-header border-0 bg-transparent px-4 py-3 donezo-workflow-card__header donezo-workflow-card__header--warm">
                  <h5 className="mb-0 d-flex align-items-center donezo-workflow-card__title donezo-workflow-card__title--lg">
                    <i className="fa fa-calendar-alt me-2 text-warning"></i>
                    Dates & Management
                  </h5>
                </div>
                <div className="card-body px-4 py-3">
                  <div className="row g-3">
                    {/* Hidden when SHOW_CREATE_POND_AGE_OF_POND is false */}
                    <div
                      className={`col-12 ${SHOW_CREATE_POND_AGE_OF_POND ? '' : 'd-none'}`}
                      aria-hidden={!SHOW_CREATE_POND_AGE_OF_POND}
                    >
                      <AgeOfPondSelector
                        value={{ ageType: formData.AgeType, ageInYears: formData.AgeInYears }}
                        onChange={handleAgeOfPondChange}
                      />
                    </div>

                    {/* Estimated initial stocking date — use Stocking Data tab after pond exists */}
                    <div
                      className={`col-12 ${SHOW_CREATE_POND_ESTIMATED_INITIAL_STOCKING_DATE ? '' : 'd-none'}`}
                      aria-hidden={!SHOW_CREATE_POND_ESTIMATED_INITIAL_STOCKING_DATE}
                    >
                      <DateWithUnknownSelector
                        value={{ dateType: formData.InitialStockingDateType, dateValue: formData.EstimatedInitialStockingDate }}
                        onChange={handleInitialStockingDateChange}
                        label="Estimated Initial Stocking Date"
                      />
                    </div>

                    {/* Hidden when SHOW_CREATE_POND_SUPPLEMENTAL_STOCKING_DATES is false — future dates also on Stocking Data tab */}
                    <div
                      className={`col-12 ${SHOW_CREATE_POND_SUPPLEMENTAL_STOCKING_DATES ? '' : 'd-none'}`}
                      aria-hidden={!SHOW_CREATE_POND_SUPPLEMENTAL_STOCKING_DATES}
                    >
                      <SupplementalStockingDates
                        value={formData.SupplementalStockingDates}
                        onChange={handleSupplementalStockingDatesChange}
                      />
                    </div>

                    {/* Hidden when SHOW_CREATE_POND_BEGIN_MANAGEMENT_DATE is false */}
                    <div
                      className={`col-12 ${SHOW_CREATE_POND_BEGIN_MANAGEMENT_DATE ? '' : 'd-none'}`}
                      aria-hidden={!SHOW_CREATE_POND_BEGIN_MANAGEMENT_DATE}
                    >
                      <label htmlFor="BeginManagementDate" className="form-label small mb-1">
                        Begin Management Date
                      </label>
                      <input
                        type="date"
                        className="form-control"
                        id="BeginManagementDate"
                        value={formData.BeginManagementDate}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            )}

            {/* Physical Characteristics Panel - RIGHT SIDE (full width on lg when Dates card hidden) */}
            <div className={`col-12 ${showDatesManagementCard ? 'col-lg-6' : 'col-lg-12'}`}>
              <div
                className={`card border-0 shadow-sm donezo-workflow-card${
                  showDatesManagementCard ? ' h-100' : ''
                }`}
              >
                <div className="card-header border-0 bg-transparent px-4 py-3 donezo-workflow-card__header donezo-workflow-card__header--green">
                  <h5 className="mb-0 d-flex align-items-center donezo-workflow-card__title donezo-workflow-card__title--lg">
                    <i className="fa fa-ruler-combined me-2 text-success"></i>
                    Physical Characteristics
                  </h5>
                </div>
                <div className="card-body px-4 py-3">
                  <div className="row g-3">
                    <div className="col-12 col-md-6">
                      <label htmlFor="Size" className="form-label small mb-1">
                        Size (acres) <span className="text-danger">*</span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="Size"
                        value={formData.Size}
                        onChange={handleInputChange}
                        min="0"
                        step="0.01"
                      />
                    </div>

                    <div className="col-12 col-md-6">
                      <label htmlFor="MaximumDepth" className="form-label small mb-1">
                        Max Depth (feet) <span className="text-danger">*</span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="MaximumDepth"
                        value={formData.MaximumDepth}
                        onChange={handleInputChange}
                        min="0"
                        step="0.01"
                      />
                    </div>

                    <div className="col-12 col-md-6">
                      <label htmlFor="AverageDepth" className="form-label small mb-1">
                        Average Depth (feet)
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="AverageDepth"
                        value={formData.AverageDepth}
                        onChange={handleInputChange}
                        min="0"
                        step="0.01"
                      />
                    </div>

                    {formData.EstimatedVolume && (
                      <div className="col-12 col-md-6">
                        <label className="form-label small mb-1">
                          Estimated Volume (acre-feet)
                        </label>
                        <input
                          type="number"
                          className="form-control donezo-effort-readonly"
                          value={formData.EstimatedVolume.toFixed(2)}
                          readOnly
                          disabled
                        />
                      </div>
                    )}

                    {/* Hidden when SHOW_CREATE_POND_AQUATIC_VEGETATION is false */}
                    <div
                      className={`col-12 ${SHOW_CREATE_POND_AQUATIC_VEGETATION ? '' : 'd-none'}`}
                      aria-hidden={!SHOW_CREATE_POND_AQUATIC_VEGETATION}
                    >
                      <label htmlFor="AquaticVegetationPresent" className="form-label small mb-1">
                        Aquatic Vegetation Present
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="AquaticVegetationPresent"
                        value={formData.AquaticVegetationPresent}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default CreatePond;